import { Dispatch } from 'redux';
import { api } from '../../../general/apiScenarios';
import { NodeFlag } from '../../../general/model';
import { Category, Project } from '../../api';
import type { RootStore } from '../../reducers';
import * as categoryActions from '../category/actions';
import { ALL_CATEGORIES_ID } from '../category/model';
import { getCategories } from '../category/selectors';
import * as filtersCacheActions from '../filtersCache/actions';
import { readDictionaryCache } from '../filtersCache/helpers';

// Top-level folders of an Asset Lab project are children of the project's `assetlab`
// node (project node -> "Asset Lab" -> categories). Project.id and Category.id are the
// Creative Hub node ids, so the folder is created through the crhub fs API.
async function findAssetLabNodeId(projectId: Project['id']): Promise<number | null> {
  const result = await api.fs.loadNodes(projectId);
  if (!result.success) return null;
  const node = result.data.response.nodes.find((n) => n.flags.includes(NodeFlag.assetlab));
  return node?.id ?? null;
}

/**
 * Creates a top-level folder in the project and selects it in the sidebar, so the
 * upload goes there and refreshAfterUpload reloads its assets. Returns the new
 * folder's node id or an error message for the user.
 */
export async function createUploadFolder(
  dispatch: Dispatch,
  getState: () => RootStore,
  projectId: Project['id'],
  name: string,
): Promise<{ nodeId: number } | { error: string }> {
  const assetLabNodeId = await findAssetLabNodeId(projectId);
  if (assetLabNodeId === null) {
    return { error: "Couldn't create the folder: the project has no Asset Lab folder." };
  }

  const result = await api.fs.createFolder(name, assetLabNodeId);
  if (!result.success) {
    return { error: `Couldn't create folder "${name}". It may already exist.` };
  }
  const node = result.data.response.node;
  const category: Category = { id: String(node.id), name: node.name, path: [] };

  // The categories dictionary is cached per project set and is not re-requested within
  // the session, so the new folder is put into both the slice and the cache.
  const categories = [...getCategories(getState()), category];
  dispatch(categoryActions.categoriesChanged({ categories }));
  const { key } = readDictionaryCache(getState(), 'category', { projectIds: [projectId] });
  dispatch(filtersCacheActions.stored({ dict: 'category', key, items: categories }));

  dispatch(
    categoryActions.setExpandedKeys({
      keys: [ALL_CATEGORIES_ID, category.id],
      saveLastState: true,
    }),
  );
  dispatch(
    categoryActions.categorySelected({ id: category.id, saveLastState: true, log: { category } }),
  );

  return { nodeId: node.id };
}
